import React, { useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { ArrowLeft, Package, DollarSign, Truck, Camera, Upload, LogIn } from 'lucide-react';
import { toast } from 'sonner';

const ShippingCalculator = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [step, setStep] = useState(1);
  const [scanMode, setScanMode] = useState(false);
  const [formData, setFormData] = useState({
    from_location: 'New York, USA',
    to_location: 'Lagos, Nigeria',
    weight_lbs: '',
    length_inches: '',
    width_inches: '',
    height_inches: '',
    pickup_address: '',
    delivery_address: ''
  });
  const [quote, setQuote] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [booking, setBooking] = useState(false);

  const calculateQuote = async () => {
    if (!formData.weight_lbs || !formData.length_inches || !formData.width_inches || !formData.height_inches) {
      toast.error('Please fill in all dimensions');
      return;
    }

    setCalculating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shipping/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          weight_lbs: parseFloat(formData.weight_lbs),
          length_inches: parseFloat(formData.length_inches),
          width_inches: parseFloat(formData.width_inches),
          height_inches: parseFloat(formData.height_inches),
          from_location: formData.from_location,
          to_location: formData.to_location
        })
      });

      const data = await response.json();
      setQuote(data);
      setStep(2);
    } catch (error) {
      toast.error('Error calculating quote');
    } finally {
      setCalculating(false);
    }
  };

  const handleCameraCapture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    toast.info('AI scanning feature coming soon! For now, please enter dimensions manually.');
    
    // Placeholder for future AI implementation
    // This would integrate with a vision API to detect object dimensions
    // For MVP, we'll set example values
    const simulatedValues = {
      weight_lbs: '10',
      length_inches: '12',
      width_inches: '8',
      height_inches: '6'
    };
    
    // Show preview that scanning is happening
    toast.success('Image captured! Enter actual dimensions below.');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    toast.info('AI scanning feature coming soon! Please enter dimensions manually.');
  };

  const createShipment = async () => {
    // Check if user is logged in
    if (!user) {
      toast.error('Please login to create a shipment');
      setTimeout(() => {
        navigate('/register', { state: { returnTo: '/shipping' } });
      }, 1500);
      return;
    }

    if (!formData.pickup_address || !formData.delivery_address) {
      toast.error('Please provide pickup and delivery addresses');
      return;
    }

    setBooking(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shipping/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          from_location: formData.from_location,
          to_location: formData.to_location,
          weight_lbs: parseFloat(formData.weight_lbs),
          dimensions: {
            length: parseFloat(formData.length_inches),
            width: parseFloat(formData.width_inches),
            height: parseFloat(formData.height_inches)
          },
          pickup_address: formData.pickup_address,
          delivery_address: formData.delivery_address
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Shipment created! Tracking number: ${data.shipment.tracking_number}`);
        navigate('/dashboard');
      } else {
        toast.error('Failed to create shipment');
      }
    } catch (error) {
      toast.error('Error creating shipment');
    } finally {
      setBooking(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] p-4 md:p-8" data-testid="shipping-calculator-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(user ? '/dashboard' : '/')}
            className="flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span style={{ fontFamily: 'Manrope' }}>Back to {user ? 'Dashboard' : 'Home'}</span>
          </button>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>Shipping Calculator</h1>
          <p className="text-white/60" style={{ fontFamily: 'Manrope' }}>Calculate shipping cost and create your shipment</p>
        </div>

        {/* Guest Browse Notice */}
        {!user && (
          <div className="mb-6 p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3">
              <LogIn className="w-5 h-5 text-[#FF3B30]" />
              <p className="text-white/80 text-sm md:text-base" style={{ fontFamily: 'Manrope' }}>
                Browsing as guest. <span className="text-[#FF3B30] font-semibold">Login required</span> to create shipment.
              </p>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="btn-primary text-sm px-6 py-2 whitespace-nowrap"
              data-testid="guest-login-btn"
            >
              Login
            </button>
          </div>
        )}

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm font-semibold ${step >= 1 ? 'text-[#FFD60A]' : 'text-white/40'}`} style={{ fontFamily: 'Manrope' }}>1. Calculate</span>
            <span className={`text-sm font-semibold ${step >= 2 ? 'text-[#FFD60A]' : 'text-white/40'}`} style={{ fontFamily: 'Manrope' }}>2. Details</span>
            <span className={`text-sm font-semibold ${step >= 3 ? 'text-[#FFD60A]' : 'text-white/40'}`} style={{ fontFamily: 'Manrope' }}>3. Confirm</span>
          </div>
          <div className="h-1 bg-white/10">
            <div className="h-full bg-[#FFD60A] transition-all duration-300" style={{ width: `${(step / 3) * 100}%` }}></div>
          </div>
        </div>

        {step === 1 && (
          <div className="card-glass p-8">
            <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Sora' }}>Package Details</h2>
            
            {/* Camera Scan Option */}
            <div className="mb-8 p-6 bg-[#FFD60A]/10 border border-[#FFD60A]/30">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Sora' }}>
                <Camera className="w-5 h-5 text-[#FFD60A]" />
                Quick Scan with Camera
              </h3>
              <p className="text-white/60 text-sm mb-4" style={{ fontFamily: 'Manrope' }}>
                Use your phone camera to scan and auto-calculate dimensions (Feature coming soon!)
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex items-center justify-center gap-2 bg-[#FFD60A] text-black hover:bg-[#EAB308] font-semibold px-6 py-3 transition-all"
                  data-testid="camera-scan-btn"
                >
                  <Camera className="w-5 h-5" />
                  Open Camera
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center justify-center gap-2 border border-[#FFD60A] text-[#FFD60A] hover:bg-[#FFD60A]/10 font-semibold px-6 py-3 transition-all"
                  data-testid="upload-image-btn"
                >
                  <Upload className="w-5 h-5" />
                  Upload Image
                </button>
              </div>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleCameraCapture}
                className="hidden"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>

            <div className="border-t border-white/10 pt-6 mb-6">
              <p className="text-white/60 text-sm text-center" style={{ fontFamily: 'Manrope' }}>
                Or enter dimensions manually below
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>From Location</label>
                <input
                  type="text"
                  className="input-base w-full"
                  value={formData.from_location}
                  onChange={(e) => setFormData({ ...formData, from_location: e.target.value })}
                  data-testid="from-location-input"
                />
              </div>
              <div>
                <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>To Location</label>
                <input
                  type="text"
                  className="input-base w-full"
                  value={formData.to_location}
                  onChange={(e) => setFormData({ ...formData, to_location: e.target.value })}
                  data-testid="to-location-input"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>Weight (lbs)</label>
              <input
                type="number"
                step="0.1"
                className="input-base w-full"
                value={formData.weight_lbs}
                onChange={(e) => setFormData({ ...formData, weight_lbs: e.target.value })}
                placeholder="Enter weight in pounds"
                data-testid="weight-input"
              />
            </div>

            <div className="mb-6">
              <label className="block text-white/80 text-sm mb-4" style={{ fontFamily: 'Manrope' }}>Dimensions (inches)</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <input
                    type="number"
                    step="0.1"
                    className="input-base w-full"
                    value={formData.length_inches}
                    onChange={(e) => setFormData({ ...formData, length_inches: e.target.value })}
                    placeholder="Length"
                    data-testid="length-input"
                  />
                </div>
                <div>
                  <input
                    type="number"
                    step="0.1"
                    className="input-base w-full"
                    value={formData.width_inches}
                    onChange={(e) => setFormData({ ...formData, width_inches: e.target.value })}
                    placeholder="Width"
                    data-testid="width-input"
                  />
                </div>
                <div>
                  <input
                    type="number"
                    step="0.1"
                    className="input-base w-full"
                    value={formData.height_inches}
                    onChange={(e) => setFormData({ ...formData, height_inches: e.target.value })}
                    placeholder="Height"
                    data-testid="height-input"
                  />
                </div>
              </div>
            </div>

            <button
              onClick={calculateQuote}
              disabled={calculating}
              className="btn-primary w-full"
              data-testid="calculate-quote-btn"
            >
              {calculating ? 'Calculating...' : 'Calculate Quote'}
            </button>
          </div>
        )}

        {step === 2 && quote && (
          <div className="space-y-6">
            {/* Quote Summary */}
            <div className="card-glass p-8 border-l-4 border-[#FFD60A]">
              <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Sora' }}>Shipping Quote</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <p className="text-white/60 text-sm mb-1" style={{ fontFamily: 'Manrope' }}>Actual Weight</p>
                  <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Sora' }}>{quote.actual_weight_lbs} lbs</p>
                </div>
                <div>
                  <p className="text-white/60 text-sm mb-1" style={{ fontFamily: 'Manrope' }}>Volumetric Weight</p>
                  <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Sora' }}>{quote.volumetric_weight_lbs} lbs</p>
                </div>
                <div>
                  <p className="text-white/60 text-sm mb-1" style={{ fontFamily: 'Manrope' }}>Billable Weight</p>
                  <p className="text-2xl font-bold text-[#FFD60A]" style={{ fontFamily: 'Sora' }}>{quote.final_weight_lbs} lbs</p>
                </div>
                <div>
                  <p className="text-white/60 text-sm mb-1" style={{ fontFamily: 'Manrope' }}>Delivery Time</p>
                  <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Sora' }}>{quote.delivery_days} days</p>
                </div>
              </div>

              <div className="border-t border-white/10 pt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/60" style={{ fontFamily: 'Manrope' }}>Rate per lb</span>
                  <span className="text-white font-semibold">${quote.rate_per_lb}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xl text-white" style={{ fontFamily: 'Sora' }}>Total Cost</span>
                  <span className="text-4xl font-bold text-[#FFD60A]" style={{ fontFamily: 'Sora' }}>${quote.base_cost_usd}</span>
                </div>
              </div>
            </div>

            {/* Address Details */}
            <div className="card-glass p-8">
              <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Sora' }}>Addresses</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>Pickup Address</label>
                  <textarea
                    className="input-base w-full h-24 py-3"
                    value={formData.pickup_address}
                    onChange={(e) => setFormData({ ...formData, pickup_address: e.target.value })}
                    placeholder="Enter complete pickup address"
                    data-testid="pickup-address-input"
                  />
                </div>
                <div>
                  <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>Delivery Address</label>
                  <textarea
                    className="input-base w-full h-24 py-3"
                    value={formData.delivery_address}
                    onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                    placeholder="Enter complete delivery address"
                    data-testid="delivery-address-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setStep(1)}
                className="border border-white/20 text-white hover:bg-white/10 px-6 py-4 flex-1 transition-all duration-300"
                data-testid="back-to-step1-btn"
              >
                Back
              </button>
              <button
                onClick={createShipment}
                disabled={booking}
                className="btn-primary flex-1"
                data-testid="create-shipment-btn"
              >
                {booking ? 'Creating...' : 'Create Shipment'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShippingCalculator;